import { useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { t, useLanguage } from '@/lib/i18n'
import { NavPill } from './NavPill'
import { activeNavIndex, primaryNavItems, type NavItem } from './nav-items'
import { useStretchIndicator } from './stretch-indicator'

/**
 * The phone navigation (760px and below, rendered instead of the top bar's nav, never together): a floating navy-900 tab
 * bar, 16px from the sides and the bottom safe area, 64px tall, with the lift shadow (APP-DESIGN-DIRECTION 4.1). Each slot
 * is a glyph over an 11px label; a white pill slides behind the active slot with the two-spring stretch and its glyph turns
 * solid. Labels may wrap in Arabic, never truncate. `aria-label` is "Primary navigation", the same as the top bar's,
 * because only one of them exists at a time. No centre "+" action: the main action lives on the screen.
 */
export function BottomBar({ items = primaryNavItems() }: { items?: NavItem[] }) {
  useLanguage()
  const { pathname } = useLocation()
  const active = activeNavIndex(items, pathname)
  const navRef = useRef<HTMLElement>(null)
  const indicatorRef = useRef<HTMLSpanElement>(null)
  useStretchIndicator({ containerRef: navRef, indicatorRef, activeIndex: active, itemSelector: '.r5-nav__item', round: '999px' })
  return (
    <nav ref={navRef} className="r5-bottombar" aria-label={t('Primary navigation')} style={{ ['--r5-cells' as string]: items.length }}>
      <span ref={indicatorRef} className="r5-bottombar__indicator" aria-hidden="true" />
      {items.map((item, index) => (
        <NavPill key={item.to} to={item.to} label={item.label()} glyph={item.glyph} active={index === active} placement="bottom" />
      ))}
    </nav>
  )
}
